import { useState } from "react";
import {
  mergeAttorneyIntoGuardianshipPrincipals,
  useAttorneyAutocompleteSearch,
} from "./attorneySearch";
import {
  createProbateAttorneySearchErrorHandler as createBondAttorneySearchErrorHandler,
} from "./probateBondAttorneyWiring";

export {
  buildProbateAttorneySearchInputHandler as buildBondAttorneySearchInputHandler,
  buildProbateGuardianAttorneySearchHelperText as buildBondGuardianAttorneySearchHelperText,
  handleProbateAttorneySelection as handleStandardPrincipalAttorneySelection,
  handleProbateGuardianManualAttorneyEntry as handleBondGuardianManualAttorneyEntry,
} from "./probateBondAttorneyWiring";

export { createBondAttorneySearchErrorHandler };

export function handleGuardianshipAttorneySelection(updateData, data, index, attorney) {
  updateData({
    ...data,
    guardians: mergeAttorneyIntoGuardianshipPrincipals(data.guardians, index, attorney),
  });
}

export function useBondGuardianAttorneySearch({ apiClient, role, lawfirmId, toast }) {
  const [attorneySearchInput, setAttorneySearchInput] = useState("");
  const {
    attorneys,
    loadingAttorneys,
    debouncedSearch,
    clearAttorneys,
  } = useAttorneyAutocompleteSearch({
    apiClient,
    role,
    lawfirmId,
    onSearchError: createBondAttorneySearchErrorHandler(toast),
  });

  return {
    attorneySearch: {
      attorneySearchInput,
      setAttorneySearchInput,
      attorneys,
      loadingAttorneys,
      debouncedSearch,
      clearAttorneys,
    },
  };
}

export function getBondGuardianAttorneyOptionLabel(option) {
  return option?.label || "";
}

export function passThroughBondGuardianAttorneyOptions(options) {
  return options;
}

export function resolveBondGuardianAttorneyValue(attorneys, guardian) {
  return attorneys.find((attorney) => attorney.value === guardian.attorney) || null;
}

export function findBondAttorneyByGuardian(attorneys, guardian) {
  if (!guardian?.attorney) {
    return null;
  }
  return attorneys.find((entry) => entry.value === guardian.attorney) || null;
}

const GUARDIANSHIP_ATTORNEY_ADDRESS_FIELDS = {
  street: "guardian_street_address_1",
  city: "guardian_city",
  state: "guardian_state",
  zip: "guardian_zip",
};

const STANDARD_ATTORNEY_ADDRESS_FIELDS = {
  street: "address",
  city: "city",
  state: "state",
  zip: "zip_code",
};

export function handleBondAttorneyHomeAddressChange(
  updateData,
  data,
  index,
  value,
  attorneys,
  addressFields
) {
  const updatedGuardians = [...data.guardians];
  const guardian = updatedGuardians[index];
  const attorney = findBondAttorneyByGuardian(attorneys, guardian);

  updatedGuardians[index] = {
    ...updatedGuardians[index],
    is_attorney_home: value,
  };

  if (value === "Yes") {
    updatedGuardians[index] = {
      ...updatedGuardians[index],
      [addressFields.street]: "",
      [addressFields.city]: "",
      [addressFields.state]: "",
      [addressFields.zip]: "",
    };
  } else if (attorney) {
    updatedGuardians[index] = {
      ...updatedGuardians[index],
      [addressFields.street]: attorney.address,
      [addressFields.city]: attorney.city,
      [addressFields.state]: attorney.state,
      [addressFields.zip]: attorney.zip_code,
    };
  }

  updateData({ ...data, guardians: updatedGuardians });
}

export function handleGuardianshipAttorneyHomeAddressChange(
  updateData,
  data,
  index,
  value,
  attorneys
) {
  handleBondAttorneyHomeAddressChange(
    updateData,
    data,
    index,
    value,
    attorneys,
    GUARDIANSHIP_ATTORNEY_ADDRESS_FIELDS
  );
}

export function handleStandardPrincipalAttorneyHomeAddressChange(
  updateData,
  data,
  index,
  value,
  attorneys
) {
  handleBondAttorneyHomeAddressChange(
    updateData,
    data,
    index,
    value,
    attorneys,
    STANDARD_ATTORNEY_ADDRESS_FIELDS
  );
}

export function buildStandardPrincipalAttorneyFormContext({
  apiClient,
  role,
  lawfirmId,
  toast,
  updateData,
  data,
  guardians,
  fieldError,
}) {
  return {
    apiClient,
    role,
    lawfirmId,
    toast,
    updateData,
    data,
    guardians,
    fieldError,
  };
}

export function buildGuardianshipAttorneyFormContext({
  apiClient,
  role,
  lawfirmId,
  toast,
  updateData,
  data,
  guardians,
  fieldError,
}) {
  return buildStandardPrincipalAttorneyFormContext({
    apiClient,
    role,
    lawfirmId,
    toast,
    updateData,
    data,
    guardians,
    fieldError,
  });
}

export function buildBondPrincipalAttorneySelectionHandler(
  updateData,
  data,
  selectionHandler
) {
  return (index, attorney) => {
    selectionHandler(updateData, data, index, attorney);
  };
}

export function buildBondGuardianAttorneyChangeHandler(onSelect, guardianIndex) {
  return (_event, newValue) => onSelect(guardianIndex, newValue);
}

export function buildBondGuardianAttorneySearchProps({
  attorneySearchInput,
  setAttorneySearchInput,
  attorneys,
  loadingAttorneys,
  debouncedSearchAttorneys,
  clearAttorneys,
  guardian,
  guardianIndex,
  updateData,
  data,
  guardians,
  onSelect,
  fieldError = "",
  required = false,
}) {
  return {
    attorneys,
    loadingAttorneys,
    attorneySearchInput,
    setAttorneySearchInput,
    debouncedSearchAttorneys,
    clearAttorneys,
    guardian,
    guardianIndex,
    updateData,
    data,
    guardians,
    onSelect,
    fieldError,
    required,
  };
}

export function buildBondPrincipalAttorneySearchSectionProps({
  apiClient,
  role,
  lawfirmId,
  toast,
  updateData,
  data,
  guardians,
  guardian,
  guardianIndex,
  selectionHandler,
  fieldError,
  required,
}) {
  return {
    apiClient,
    role,
    lawfirmId,
    toast,
    updateData,
    data,
    guardians,
    guardian,
    guardianIndex,
    selectionHandler,
    fieldError: fieldError || "",
    required: !!required,
  };
}
